import { create } from 'zustand';
import type { ShipType } from '@/net/protocol';

type Screen =
  | 'home'
  | 'loading'
  | 'match'
  | 'match_end';

interface UIState {
  // Current screen
  screen: Screen;
  previousScreen: Screen | null;

  // Ship selection
  selectedShipType: ShipType;

  // Overlays
  showSettings: boolean;
  showShop: boolean;
  showInventory: boolean;
  showConnectionLost: boolean;

  // Match UI state
  showDeathScreen: boolean;
  showMatchStats: boolean;
  showMinimap: boolean;

  // Server error notification
  serverError: { code: string; message: string } | null;

  // Settings values
  settings: {
    masterVolume: number;
    sfxVolume: number;
    musicVolume: number;
    graphicsQuality: 'low' | 'medium' | 'high';
    dynamicResolution: boolean;
    showFps: boolean;
    cloudsEnabled: boolean;
  };

  // Scene loading progress (0-100)
  loadProgress: number;
  loadLabel: string;

  // Terrain noise tuning (drives GPU uniforms in real-time)
  terrain: {
    mountainScale: number;
    hillScale: number;
    ridgeSharpness: number;
    erosionStrength: number;
    beachWidth: number;
    shoreSmooth: number;
  };

  // Actions
  setScreen: (screen: Screen) => void;
  goBack: () => void;

  setSelectedShipType: (shipType: ShipType) => void;

  openSettings: () => void;
  closeSettings: () => void;
  toggleSettings: () => void;

  openShop: () => void;
  closeShop: () => void;

  openInventory: () => void;
  closeInventory: () => void;

  setConnectionLost: (lost: boolean) => void;

  setDeathScreen: (show: boolean) => void;
  setMatchStats: (show: boolean) => void;
  toggleMinimap: () => void;

  setServerError: (error: { code: string; message: string } | null) => void;
  clearServerError: () => void;

  setLoadProgress: (progress: number, label: string) => void;

  updateSettings: (partial: Partial<UIState['settings']>) => void;
  updateTerrain: (partial: Partial<UIState['terrain']>) => void;
  resetTerrain: () => void;
}

export const useUIStore = create<UIState>((set, get) => ({
  screen: 'loading',
  previousScreen: null,

  selectedShipType: 'fighter',

  showSettings: false,
  showShop: false,
  showInventory: false,
  showConnectionLost: false,

  showDeathScreen: false,
  showMatchStats: false,
  showMinimap: true,

  serverError: null,

  loadProgress: 0,
  loadLabel: 'Initializing...',

  settings: {
    masterVolume: 0.8,
    sfxVolume: 1.0,
    musicVolume: 0.6,
    graphicsQuality: 'high',
    dynamicResolution: false,
    showFps: false,
    cloudsEnabled: true,
  },

  terrain: {
    mountainScale: 1.0,
    hillScale: 1.0,
    ridgeSharpness: 0.4,
    erosionStrength: 0.3,
    beachWidth: 1.0,
    shoreSmooth: 8.0,
  },

  // Actions
  setScreen: (screen) => set({ 
    screen, 
    previousScreen: get().screen,
    // Close overlays when changing main screen
    showSettings: false,
    showShop: false,
    showInventory: false,
  }),

  goBack: () => {
    const prev = get().previousScreen;
    if (prev) {
      set({ screen: prev, previousScreen: null });
    }
  },

  setSelectedShipType: (shipType) => set({ selectedShipType: shipType }),

  openSettings: () => set({ showSettings: true }),
  closeSettings: () => set({ showSettings: false }),
  toggleSettings: () => set((s) => ({ showSettings: !s.showSettings })),

  openShop: () => set({ showShop: true }),
  closeShop: () => set({ showShop: false }),

  openInventory: () => set({ showInventory: true }),
  closeInventory: () => set({ showInventory: false }),

  setConnectionLost: (lost) => set({ showConnectionLost: lost }),

  setDeathScreen: (show) => set({ showDeathScreen: show }),
  setMatchStats: (show) => set({ showMatchStats: show }),
  toggleMinimap: () => set((s) => ({ showMinimap: !s.showMinimap })),

  setServerError: (error) => set({ serverError: error }),
  clearServerError: () => set({ serverError: null }),

  setLoadProgress: (progress, label) => set({ loadProgress: progress, loadLabel: label }),

  updateSettings: (partial) => set((s) => ({
    settings: { ...s.settings, ...partial },
  })),

  updateTerrain: (partial) => set((s) => ({
    terrain: { ...s.terrain, ...partial },
  })),

  resetTerrain: () => set({
    terrain: {
      mountainScale: 1.0,
      hillScale: 1.0,
      ridgeSharpness: 0.4,
      erosionStrength: 0.3,
      beachWidth: 1.0,
      shoreSmooth: 8.0,
    },
  }),
}));
